import * as SentryReact from "@sentry/react";
import * as Sentry from "@sentry/react-native";
import React, { ReactNode } from "react";
import { Platform } from "react-native";

import { applyRelayFetchContextToSentryScope } from "@whoosh/relay/src";

import { useCheckForUpdates } from "../../../utils/expoUpdates/useCheckForUpdates";

type PlatformSpecificErrorBoundaryProps = {
  fallback: (
    eventId: string,
    error: unknown,
    resetError: () => void
  ) => React.JSX.Element;
  children: ReactNode | null;
};

/**
 * Allows you to wrap an error boundary and not worry about which platform specific
 * sentry boundary to render
 */
export const PlatformSpecificErrorBoundary: React.FC<
  PlatformSpecificErrorBoundaryProps
> = ({ fallback, children }: PlatformSpecificErrorBoundaryProps) => {
  if (Platform.OS === "web") {
    return (
      <SentryReact.ErrorBoundary
        beforeCapture={applyRelayFetchContextToSentryScope}
        fallback={({ error, eventId, resetError }) =>
          fallback(eventId, error, resetError)
        }
      >
        {children}
      </SentryReact.ErrorBoundary>
    );
  } else {
    return (
      <Sentry.ErrorBoundary
        beforeCapture={applyRelayFetchContextToSentryScope}
        fallback={({ error, eventId, resetError }) => {
          return (
            <FallbackWrapperToCheckUpdates
              eventId={eventId}
              error={error}
              resetError={resetError}
              fallbackRenderer={fallback}
            />
          );
        }}
      >
        {children}
      </Sentry.ErrorBoundary>
    );
  }
};

/**
 *
 * Whenever we render the fallback component, we quick check for updates in case that will resolve the error
 */
const FallbackWrapperToCheckUpdates = ({
  eventId,
  error,
  resetError,
  fallbackRenderer,
}: {
  eventId: string;
  error: unknown;
  resetError: () => void;
  fallbackRenderer: (
    eventId: string,
    error: unknown,
    resetError: () => void
  ) => React.JSX.Element;
}) => {
  useCheckForUpdates();

  return <>{fallbackRenderer(eventId, error, resetError)}</>;
};
